import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GetApiBase, MaybeForceClientRefresh } from '../utils/api';
import PinKeypad from '../components/PinKeypad';

const CAPTURE_STORY = [
  'The lock on the always-on container gives up with a polite click. Not a roar. A courtesy.',
  'Inside: one folding chair, one damp question mark, and Edward Nashton mid-gloat like a man who booked the wrong conference room.',
  'You do not punch him. You file him. Zip ties. Clipboards. The bureaucracy of justice smelling faintly of ozone and bad coffee.',
  'He asks if this counts as a team-building exercise. You tell him Nutanix Kubernetes Platform handled the halls. Goon42 handled the chatter. You handled the last yard.',
  'Nashton is booked. The Funhouse lights dim to a respectful shrug. Somewhere, a Challenge Coin waits like a poorly tempered medal.',
];

function PrefersReducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function StageForAge(age) {
  if (age <= 0) return 'is-lead';
  if (age === 1) return 'is-trail';
  return 'is-settled';
}

export default function EdwardNashton() {
  const navigate = useNavigate();
  const { AdoptToken } = useAuth();
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState('');
  const [coinStatus, setCoinStatus] = useState('none');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [phase, setPhase] = useState('login'); // login | shame | story | claimed
  const [shame, setShame] = useState('');
  const [visibleCount, setVisibleCount] = useState(0);
  const [settleExtra, setSettleExtra] = useState(0);
  const [storyDone, setStoryDone] = useState(false);
  const [claimBusy, setClaimBusy] = useState(false);
  const [claimDetail, setClaimDetail] = useState('');
  const timerRef = useRef(null);

  const chatHref = '/chat';

  useEffect(() => () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    if (phase !== 'story') return undefined;
    if (PrefersReducedMotion()) {
      setVisibleCount(CAPTURE_STORY.length);
      setSettleExtra(2);
      setStoryDone(true);
      return undefined;
    }
    setVisibleCount(0);
    setSettleExtra(0);
    setStoryDone(false);
    let count = 0;
    const Tick = () => {
      count += 1;
      setVisibleCount(count);
      if (count < CAPTURE_STORY.length) {
        timerRef.current = window.setTimeout(Tick, 1600);
        return;
      }
      timerRef.current = window.setTimeout(() => {
        setSettleExtra(2);
        setStoryDone(true);
      }, 900);
    };
    timerRef.current = window.setTimeout(Tick, 700);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [phase]);

  const visibleParas = useMemo(
    () => CAPTURE_STORY.slice(0, visibleCount),
    [visibleCount],
  );

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setShame('');
    try {
      const res = await fetch(`${GetApiBase()}/gate/edward-nashton`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), pin }),
      });
      MaybeForceClientRefresh(res);
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        setError(typeof data.detail === 'string' ? data.detail : 'Invalid username or PIN');
        return;
      }
      if (!res.ok) {
        setError(typeof data.detail === 'string' ? data.detail : 'Gate refused.');
        return;
      }
      if (data.status === 'shame') {
        setPhase('shame');
        setShame(data.message || 'Wrong shift.');
        setYoutubeUrl(data.youtube_url || '');
        return;
      }
      if (data.status === 'ok') {
        setToken(data.access_token || '');
        setCoinStatus(data.coin_status || 'none');
        setYoutubeUrl(data.youtube_url || '');
        if (data.coin_status === 'claimed' || data.coin_status === 'pending_booth' || data.coin_status === 'awaiting_email') {
          setPhase('claimed');
          setStoryDone(true);
          setVisibleCount(CAPTURE_STORY.length);
        } else {
          setPhase('story');
        }
        return;
      }
      setError('Gate refused.');
    } catch {
      setError('Could not reach the Funhouse. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleClaim = async () => {
    if (!token || claimBusy) return;
    setClaimBusy(true);
    setClaimDetail('');
    try {
      const res = await fetch(`${GetApiBase()}/game/claim-coin-pending`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: '{}',
      });
      MaybeForceClientRefresh(res);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setClaimDetail(typeof data.detail === 'string' ? data.detail : 'Claim refused.');
        return;
      }
      if (!data.ok) {
        setClaimDetail(data.detail || 'No coins left.');
        return;
      }
      setCoinStatus(data.progress?.coin_status || 'awaiting_email');
      setPhase('claimed');
      // Stay in this SPA shell (esp. iOS Home Screen). Full assign/reload can spawn a 2nd Chat.
      try {
        await AdoptToken(token, username.trim());
      } catch {
        /* still navigate; Chat may re-auth */
      }
      navigate('/chat', { replace: true });
    } catch {
      setClaimDetail('Could not reach the Funhouse.');
    } finally {
      setClaimBusy(false);
    }
  };

  return (
    <div className="edward-page">
      <main className="edward-panel">
        <div className="edward-badge">Office of Edward Nigma</div>
        <h1 className="edward-title">/Edward-Nashton</h1>
        <p className="edward-sub">
          Use your Riddler Game login. Challenge 4 only. The alley door does not do appointments.
        </p>

        {phase === 'login' && (
          <form className="edward-form" onSubmit={handleLogin}>
            <label htmlFor="edward-user">Username</label>
            <input
              id="edward-user"
              autoComplete="username"
              autoCapitalize="off"
              spellCheck={false}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
            <label htmlFor="edward-pin">PIN</label>
            <input
              id="edward-pin"
              type="text"
              inputMode="none"
              autoComplete="current-password"
              maxLength={10}
              value={pin}
              onChange={(e) => setPin((e.target.value || '').replace(/\D/g, '').slice(0, 10))}
              required
            />
            <PinKeypad value={pin} onChange={setPin} maxLength={10} disabled={busy} />
            <button className="edward-btn" type="submit" disabled={busy || pin.length < 4}>
              {busy ? 'Checking…' : 'Open the office'}
            </button>
          </form>
        )}

        {phase === 'shame' && (
          <section className="edward-shame">
            <p>{shame}</p>
            {youtubeUrl && (
              <p className="edward-muted">
                Misplaced footage may still be elsewhere. Staff keep a decoy reel for tourists.
              </p>
            )}
            <button className="edward-btn edward-btn-ghost" type="button" onClick={() => setPhase('login')}>
              Try another shift
            </button>
          </section>
        )}

        {(phase === 'story' || phase === 'claimed') && (
          <section className="edward-success">
            <div className="edward-bubble">
              <span className="edward-narrator-label">Narrator</span>
              <div className="edward-reveal" aria-live="polite">
                {visibleParas.map((para, index) => {
                  const age = visibleCount + settleExtra - (index + 1);
                  return (
                    <p key={para} className={`edward-line ${StageForAge(age)}`}>
                      {para}
                    </p>
                  );
                })}
              </div>
            </div>

            {storyDone && coinStatus === 'none' && phase === 'story' && (
              <button className="edward-btn" type="button" onClick={handleClaim} disabled={claimBusy}>
                {claimBusy ? 'Stamping…' : 'Claim Challenge Coin'}
              </button>
            )}

            {(phase === 'claimed' || coinStatus !== 'none') && (
              <div className="edward-next">
                <strong>Coin claim started.</strong>
                <p>
                  Open Funhouse chat for the last formality (work email), then walk to the booth.
                  A staffer will finish the paperwork. Do not shout secret words into the void.
                </p>
                <Link className="edward-btn" to="/chat" replace>
                  Open chat
                </Link>
              </div>
            )}

            {claimDetail && <p className="edward-err">{claimDetail}</p>}
          </section>
        )}

        {error && <p className="edward-err">{error}</p>}

        <p className="edward-footer">
          Lost? <Link to={chatHref} replace>Back to chat</Link>
        </p>
      </main>
    </div>
  );
}
